import { createContext, useContext, ReactNode } from 'react';
import { useTheme as useThemeHook, ThemeMode, ColorTheme, Density } from '@/hooks/useTheme';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
  density: Density;
  setDensity: (density: Density) => void;
  appliedMode: 'light' | 'dark' | 'public-media';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider component that wraps the application and provides
 * theme state to all child components.
 *
 * Manages:
 * - Light/Dark/System mode
 * - Color themes (Zinc, Blue, Green, Orange, Rose, Violet)
 * - Density (Comfortable, Compact)
 *
 * All preferences are persisted to localStorage.
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useThemeHook();

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context.
 * Must be used within a ThemeProvider.
 */
export function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
